import React, { useState } from 'react';

import { ModalDefault } from '../../../../components/ModalDefault/ModalDefault';
import { Button } from '../../../../components/Button/Button';
import { Loading } from '../../../../components/Loading/Loading';
import { pushNotify } from '../../../../components/Notifications';
import {
  useDeleteTgSessionMutation,
  usePatchTgSessionMutation,
  useSendTgCodeMutation,
} from '../../../../redux/services/TgSessionsApi';
import { type TgSession } from '../../../../redux/types/TgSessionsTypes';
import SessionLoginModal, { type LoginMode } from './SessionLoginModal';
import EditSessionModal from './EditSessionModal';
import MessagesModal from './MessagesModal';
import { getErrorText } from './helpers';
import styles from '../TgSessions.module.css';

const SessionActions = ({ session }: { session: TgSession }) => {
  const [loginActive, setLoginActive] = useState(false);
  const [loginSession, setLoginSession] = useState<TgSession>(session);
  const [loginMode, setLoginMode] = useState<LoginMode>('code');
  const [editActive, setEditActive] = useState(false);
  const [messagesActive, setMessagesActive] = useState(false);
  const [deleteActive, setDeleteActive] = useState(false);
  const [sendCode, { isLoading: isSending }] = useSendTgCodeMutation();
  const [patchSession, { isLoading: isPatching }] = usePatchTgSessionMutation();
  const [deleteSession, { isLoading: isDeleting }] = useDeleteTgSessionMutation();

  const requestCode = async () => {
    try {
      const sent = await sendCode(session.id).unwrap();
      setLoginSession(sent);
      setLoginMode('code');
      setLoginActive(true);
    } catch (error) {
      pushNotify(getErrorText(error, 'Не удалось отправить код'), 'Ошибка');
    }
  };

  const toggleListening = async () => {
    try {
      await patchSession({ id: session.id, is_active: !session.is_active }).unwrap();
    } catch (error) {
      pushNotify(getErrorText(error, 'Не удалось изменить состояние'), 'Ошибка');
    }
  };

  if (isSending || isPatching || isDeleting) return <Loading />;

  const awaitingInput = session.status === 'CODE_SENT' || session.status === 'PASSWORD_REQUIRED';
  const needsLogin =
    session.status === 'NEW' || session.status === 'DEAD' || session.status === 'QR_PENDING';

  return (
    <>
      <div className={styles.actions}>
        {awaitingInput && (
          <span
            style={{ color: '#1890FF' }}
            onClick={() => {
              setLoginSession(session);
              setLoginMode('code');
              setLoginActive(true);
            }}
          >
            Ввести код
          </span>
        )}
        {(needsLogin || awaitingInput) && (
          <span style={{ color: '#1890FF' }} onClick={requestCode}>
            {awaitingInput ? 'Отправить код заново' : 'Подключить'}
          </span>
        )}
        {(needsLogin || awaitingInput) && (
          <span
            style={{ color: '#1890FF' }}
            onClick={() => {
              setLoginSession(session);
              setLoginMode('qr');
              setLoginActive(true);
            }}
          >
            Войти по QR
          </span>
        )}
        {(session.status === 'ACTIVE' || session.status === 'DISABLED') && (
          <span style={{ color: session.is_active ? '#E8A317' : '#2FB47C' }} onClick={toggleListening}>
            {session.is_active ? 'Приостановить' : 'Возобновить'}
          </span>
        )}
        <span style={{ color: '#1890FF' }} onClick={() => setEditActive(true)}>
          Реквизиты
        </span>
        <span style={{ color: '#1890FF' }} onClick={() => setMessagesActive(true)}>
          Сообщения
        </span>
        <span style={{ color: '#FE5B37' }} onClick={() => setDeleteActive(true)}>
          Удалить
        </span>
      </div>
      {loginActive && (
        <SessionLoginModal
          active={loginActive}
          setActive={setLoginActive}
          session={loginSession}
          mode={loginMode}
        />
      )}
      {editActive && <EditSessionModal active={editActive} setActive={setEditActive} session={session} />}
      {messagesActive && <MessagesModal active={messagesActive} setActive={setMessagesActive} session={session} />}
      <ModalDefault
        title={'Удалить Telegram-аккаунт?'}
        text={'Сессия будет разорвана, уведомления с этого аккаунта перестанут читаться.'}
        titleMargin={'8px'}
        textMargin={'24px'}
        active={deleteActive}
        setActive={setDeleteActive}
      >
        <Button text={'Отмена'} filled={false} style={{ marginRight: '8px' }} action={() => setDeleteActive(false)} />
        <Button
          text={'Удалить'}
          action={async () => {
            try {
              await deleteSession(session.id).unwrap();
              setDeleteActive(false);
            } catch (error) {
              pushNotify(getErrorText(error, 'Не удалось удалить'), 'Ошибка');
            }
          }}
        />
      </ModalDefault>
    </>
  );
};

export default SessionActions;
